import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Panel, Margin, Text, Drawer, Form, FormInput, Button } from 'shared/components'
import { ROLES } from 'constants/account.roles'

import { UserPersonalForm } from './components/userPersonalForm'
import { BeneficiaryPersonalForm } from './components/beneficiaryPersonalForm'
import { SponsorPersonalForm } from './components/sponsoryPersonalForm'
import { ChangePasswordForm } from './components/changePasswordForm'
import { ChangePasswordNavigation } from './components/changePasswordNavigation'
import { SignOut } from './components/signOut'
import { DonationsDrawer } from './components/donationsDrawer'
import { MobileProfileLayout } from './components/mobileProfileLayout'
import { MobileManageAccountSection } from './components/mobileManageAccountSection'
import { MobileDeleteAccountSection } from './components/mobileDeleteAccountSection'
import { AddressInfo } from './components/addressInfo'

const ProfileComponent = ({
  entity,
  onUpdateProfile,
  onChangePassword,
  onDeleteAccount,
  isLoading,
  appConfig,
}) => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkScreenSize = () => {
      // Show mobile layout for devices up to 767px (phones)
      const mobile = window.innerWidth <= 767
      setIsMobile(mobile)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)

    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const getForm = ({ role }) => {
    switch (role) {
      case ROLES.BENEFICIARY:
        return (
          <BeneficiaryPersonalForm
            initialValues={entity}
            onSubmit={onUpdateProfile}
            isLoading={isLoading}
          />
        )
      case ROLES.SPONSOR:
        return (
          <SponsorPersonalForm
            initialValues={entity}
            onSubmit={onUpdateProfile}
            isLoading={isLoading}
          />
        )
      case ROLES.USER:
        return (
          <UserPersonalForm
            initialValues={entity}
            onSubmit={onUpdateProfile}
            isLoading={isLoading}
          />
        )
      default:
        return (
          <UserPersonalForm
            initialValues={entity}
            onSubmit={onUpdateProfile}
            isLoading={isLoading}
          />
        )
    }
  }

  const isDonationReceiptsAllowed =
    entity.role === ROLES.USER ||
    entity.role === ROLES.SPONSOR ||
    entity.role === ROLES.ADMIN

  const handleDeleteAccount = () => {
    onDeleteAccount()
  }

  // Render mobile layout for mobile devices
  if (isMobile) {
    return (
      <MobileProfileLayout
        entity={entity}
        onUpdateProfile={onUpdateProfile}
        onChangePassword={onChangePassword}
        isLoading={isLoading}
      >
        <Margin tb={1} lr={2}>
          <Panel>
            <Text as="h1" size={16} bold>
              Account details
            </Text>
          </Panel>
        </Margin>
        <Margin tb={2} lr={4}>
          {isDonationReceiptsAllowed && <DonationsDrawer />}
          {getForm(entity)}
          <AddressInfo />
        </Margin>
        <MobileManageAccountSection />
        <Margin tb={0} lr={4}>
          <ChangePasswordNavigation />
          <SignOut />
        </Margin>
        <MobileDeleteAccountSection onDeleteClick={handleDeleteAccount} />
      </MobileProfileLayout>
    )
  }

  // Render desktop layout for larger screens
  return (
    <>
      <Margin tb={4} lr={5}>
        <Panel>
          <Text as="h1" size={18} bold>
            Account details
          </Text>
        </Panel>
      </Margin>
      <Margin tb={5} lr={5}>
        {isDonationReceiptsAllowed && <DonationsDrawer />}
        {getForm(entity)}
        <AddressInfo />
        <ChangePasswordForm onSubmit={onChangePassword} isLoading={isLoading} />
      </Margin>
      <Margin tb={5} lr={5}>
        <SignOut />
      </Margin>

      {/* Address Data Drawer */}
      <Drawer
        isActive={appConfig.drawerState === 'addressData'}
        hideDrawer={() => appConfig.toggleDrawer('')}
      >
        {appConfig.drawerState === 'addressData' && (
          <Panel>
            <Margin bottom={5}>
              <Text as="h1" size={18} bold>
                Address Data
              </Text>
            </Margin>
            <Margin tb={1}>
              <Text as="label" size={11}>Address Line 1</Text>
              <FormInput placeholder="Street address, P.O. box" />
            </Margin>
            <Margin tb={1}>
              <Text as="label" size={11}>Address Line 2</Text>
              <FormInput placeholder="Apartment, suite, unit, building, floor" />
            </Margin>
            <Margin tb={1}>
              <Text as="label" size={11}>City/Town</Text>
              <FormInput placeholder="City or town" />
            </Margin>
            <Margin tb={1}>
              <Text as="label" size={11}>State/Province/Region</Text>
              <FormInput placeholder="State, province or region" />
            </Margin>
            <Margin tb={1}>
              <Text as="label" size={11}>Postal Code/ZIP Code</Text>
              <FormInput placeholder="Postal or ZIP code" />
            </Margin>
            <Margin bottom={2} top={3}>
              <Button medium>Save changes</Button>
            </Margin>
          </Panel>
        )}
      </Drawer>
    </>
  )
}

ProfileComponent.propTypes = {
  entity: PropTypes.shape().isRequired,
  onUpdateProfile: PropTypes.func.isRequired,
  onChangePassword: PropTypes.func.isRequired,
  onDeleteAccount: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  appConfig: PropTypes.shape(),
}

export default ProfileComponent
